/* eslint-disable */
import { Injectable, inject } from "@angular/core";
import { FuseNavigationItem } from "@fuse/components/navigation";

import { VenueService } from "../../../shared/services/venue.service";
import { AccountService } from "../../../modules/admin/settings/account/account.service"; // Import AccountsService
import { BehaviorSubject, Observable, map, switchMap, of } from "rxjs";

// ----------------------
// Static exports (needed for api.ts mock)
// ----------------------
export const defaultNavigation: FuseNavigationItem[] = [
  {
    id: "overview",
    title: "Dashboards",
    type: "group",
    children: [
      {
        id: "home",
        title: "Bookings Overview",
        type: "basic",
        icon: "heroicons_outline:presentation-chart-line",
        link: "/overview",
      },
      {
        id: "players-overview",
        title: "Players Overview",
        type: "basic",
        icon: "heroicons_outline:user-group",
        link: "/players-overview",
      },
      {
        id: "venues",
        title: "Venues",
        type: "collapsable",
        icon: "location_city",
        link: "/venues",
        children: [], // gets replaced dynamically
      },
      {
        id: "bookings",
        title: "Bookings",
        type: "basic",
        icon: "calendar_today",
        link: "/bookings",
      },
      {
        id: "report",
        title: "Report",
        type: "basic",
        icon: "mat_outline:area_chart",
        link: "/reports",
      },
      {
        id: "audit-report",
        title: "Audit Report",
        type: "basic",
        icon: "mat_outline:bar_chart",
        link: "/audit-reports",
      },
    ],
  },
];

export const compactNavigation: FuseNavigationItem[] = [
  {
    id: "example",
    title: "Example",
    type: "basic",
    icon: "heroicons_outline:chart-pie",
    link: "/example",
  },
];

export const futuristicNavigation: FuseNavigationItem[] = [
  {
    id: "example",
    title: "Example",
    type: "basic",
    icon: "heroicons_outline:chart-pie",
    link: "/example",
  },
];

export const horizontalNavigation: FuseNavigationItem[] = [
  {
    id: "example",
    title: "Example",
    type: "basic",
    icon: "heroicons_outline:chart-pie",
    link: "/example",
  },
];

// ----------------------
// Dynamic service for runtime
// ----------------------
@Injectable({
  providedIn: "root",
})
export class NavigationData {
  private venueService = inject(VenueService);
  private accountsService = inject(AccountService);

  private _navigation = new BehaviorSubject<FuseNavigationItem[]>(
    defaultNavigation,
  );
  navigation$ = this._navigation.asObservable();

  constructor() {
    this.loadNavigationBasedOnRole();
  }

  private loadNavigationBasedOnRole(): void {
    this.accountsService
      .getCurrentUser()
      .pipe(
        switchMap((response: any) => {
          // Extract user data from the API response
          const userData = response?.data;
          const userRole = userData?.role;

          console.log("User Role:", userRole); // For debugging

          // Staff use the assigned-venue booking workspace only.
          if (userRole === "staff") {
            return this.createNavigationWithoutVenues();
          } else {
            // For other roles (admin, manager, etc.), load venues normally
            return this.loadVenuesWithNavigation();
          }
        }),
      )
      .subscribe({
        next: (navigation: FuseNavigationItem[]) => {
          this._navigation.next(navigation);
        },
        error: (error) => {
          console.error("Error loading navigation:", error);
          // Fallback to default navigation without venues on error
          this.createNavigationWithoutVenues().subscribe((fallbackNav) => {
            this._navigation.next(fallbackNav);
          });
        },
      });
  }

  private createNavigationWithoutVenues(): Observable<FuseNavigationItem[]> {
    // Keep navigation aligned with the backend's booking-operator boundary.
    const navWithoutVenues = JSON.parse(JSON.stringify(defaultNavigation));

    // Direct URLs remain protected by backend authorization.
    navWithoutVenues[0].children = navWithoutVenues[0].children.filter(
      (item: FuseNavigationItem) => item.id === "bookings",
    );

    return of(navWithoutVenues);
  }

  private loadVenuesWithNavigation(): Observable<FuseNavigationItem[]> {
    return this.venueService.getVenues().pipe(
      map((venues: any[]) => {
        const venueItems: FuseNavigationItem[] = venues.map((venue, index) => ({
          id: `venue-${index + 1}`,
          title: venue.name,
          type: "basic",
          link: "", // empty for now
        }));

        // Create a deep copy of defaultNavigation
        const nav = JSON.parse(JSON.stringify(defaultNavigation));
        const venuesNav = nav[0].children?.find((item) => item.id === "venues");

        if (venuesNav) {
          venuesNav.children = venueItems;
        }

        return nav;
      }),
    );
  }

  // Optional: Method to refresh navigation if user changes
  refreshNavigation(): void {
    this.loadNavigationBasedOnRole();
  }
}
